import { format } from 'date-fns';
import { CalendarDays, ChevronLeft, ChevronRight } from 'lucide-react';
import { useState } from 'react';
import { cn } from '@/lib/utils';
import { controlSurface } from './control-styles';
import { Popover, PopoverContent, PopoverTrigger } from './popover';

const MONTHS = Array.from({ length: 12 }, (_, index) => format(new Date(2000, index, 1), 'MMM'));

export function monthLabel(value: string): string {
  const [year, month] = value.split('-').map(Number);
  if (!year || !month) return '';
  return format(new Date(year, month - 1, 1), 'MMMM yyyy');
}

export function MonthPicker({
  value,
  onChange,
  min,
  max,
  disabled,
  id,
  className,
  'aria-label': ariaLabel,
}: {
  value: string;
  onChange: (value: string) => void;
  min?: string;
  max?: string;
  disabled?: boolean;
  id?: string;
  className?: string;
  'aria-label'?: string;
}) {
  const [open, setOpen] = useState(false);
  const currentYear = Number(value.slice(0, 4)) || new Date().getFullYear();
  const [year, setYear] = useState(currentYear);

  const within = (candidate: string) => (!min || candidate >= min) && (!max || candidate <= max);

  return (
    <Popover open={open} onOpenChange={(next) => { setOpen(next); if (next) setYear(currentYear); }}>
      <PopoverTrigger asChild>
        <button
          type="button"
          id={id}
          disabled={disabled}
          aria-label={ariaLabel}
          className={cn(controlSurface, 'flex h-9 w-full items-center justify-between gap-2 px-3 text-sm', className)}
        >
          <span className={cn(!value && 'text-muted-foreground')}>{monthLabel(value) || 'Pick a month'}</span>
          <CalendarDays className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64 p-3">
        <div className="mb-2 flex items-center justify-between">
          <button type="button" className="grid h-7 w-7 place-items-center rounded-md hover:bg-accent focus-ring" onClick={() => setYear(year - 1)} aria-label="Previous year">
            <ChevronLeft className="h-4 w-4 rtl:rotate-180" aria-hidden />
          </button>
          <span className="text-sm font-medium tabular">{year}</span>
          <button type="button" className="grid h-7 w-7 place-items-center rounded-md hover:bg-accent focus-ring" onClick={() => setYear(year + 1)} aria-label="Next year">
            <ChevronRight className="h-4 w-4 rtl:rotate-180" aria-hidden />
          </button>
        </div>
        <div className="grid grid-cols-3 gap-1">
          {MONTHS.map((label, index) => {
            const candidate = `${year}-${String(index + 1).padStart(2, '0')}`;
            const selected = candidate === value;
            return (
              <button
                key={candidate}
                type="button"
                disabled={!within(candidate)}
                onClick={() => {
                  onChange(candidate);
                  setOpen(false);
                }}
                className={cn(
                  'h-8 rounded-md text-sm transition-colors focus-ring disabled:opacity-40',
                  selected ? 'bg-primary text-primary-foreground' : 'hover:bg-accent',
                )}
              >
                {label}
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
